import { authService } from '@/core/auth/export';
import notificationApi from '@/core/notifications/api/notification-api';
import { Contact } from '@/core/notifications/model';
import {
  guessDeviceLabel,
  isIosDevice,
  isPushSupported,
  isSafariOnMacNeedingInstall,
  isStandalone,
  urlBase64ToUint8Array,
} from '@/shared/util/export';

export type PushStatus =
  'unsupported' | 'needs-install' | 'checking' | 'permission-denied' | 'off' | 'on' | 'error';

// Apple gates real Web Push behind installation (Home Screen on iOS, Dock on
// macOS Safari) — outside that, pushManager calls don't reject, they hang.
// Must be checked before any navigator.serviceWorker/pushManager access.
function needsInstallForPush(): boolean {
  return !isStandalone() && (isIosDevice() || isSafariOnMacNeedingInstall());
}

async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function getCurrentDeviceEndpoint(): Promise<string | null> {
  if (!isPushSupported() || needsInstallForPush()) return null;
  const subscription = await getCurrentSubscription();
  return subscription?.endpoint ?? null;
}

function findContactForSubscription(endpoint: string, contacts: Contact[]) {
  return contacts.find(
    contact => contact.channelKey === 'push' && contact.config.endpoint === endpoint,
  );
}

export async function getPushSubscriptionStatus(contacts: Contact[]): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported';
  if (needsInstallForPush()) return 'needs-install';
  if (Notification.permission === 'denied') return 'permission-denied';

  const subscription = await getCurrentSubscription();
  if (!subscription) return 'off';

  const contact = findContactForSubscription(subscription.endpoint, contacts);
  if (contact) return 'on';

  // Orphaned local subscription — its server-side contact is gone (e.g. removed from another device).
  await subscription.unsubscribe();
  return 'off';
}

function buildPushConfig(
  subscription: PushSubscription,
  deviceLabel?: string,
): { endpoint: string; p256dh: string; auth: string; deviceLabel: string } {
  const { endpoint, keys } = subscription.toJSON() as {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  };
  return {
    endpoint,
    p256dh: keys.p256dh,
    auth: keys.auth,
    deviceLabel: deviceLabel?.trim() || guessDeviceLabel(),
  };
}

export async function enablePushSubscription(deviceLabel?: string): Promise<PushStatus> {
  if (needsInstallForPush()) return 'needs-install';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'permission-denied';

  const registration = await navigator.serviceWorker.ready;
  let subscription;
  try {
    const publicKey = await notificationApi.getPushVapidPublicKey();
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    await notificationApi.addContact('push', buildPushConfig(subscription, deviceLabel));
    return 'on';
  } catch (error: any) {
    if (error?.response?.status === 409) return 'on';
    await subscription?.unsubscribe();
    return 'error';
  }
}

export async function renameDeviceContact(contact: Contact, label: string): Promise<void> {
  await notificationApi.patchContact(contact.id, { config: { deviceLabel: label } });
}

export async function renameThisDevice(label: string, contacts: Contact[]): Promise<PushStatus> {
  const subscription = await getCurrentSubscription();
  if (!subscription) return 'off';

  const existing = findContactForSubscription(subscription.endpoint, contacts);
  if (!existing) return 'off';

  try {
    await renameDeviceContact(existing, label);
    return 'on';
  } catch {
    return 'error';
  }
}

export async function disablePushSubscription(contacts: Contact[]): Promise<PushStatus> {
  const subscription = await getCurrentSubscription();
  if (!subscription) return 'off';

  try {
    const contact = findContactForSubscription(subscription.endpoint, contacts);
    if (contact) await notificationApi.deleteContact(contact.id);
  } catch (error: any) {
    if (error?.response?.status !== 404) return 'error';
  }

  await subscription.unsubscribe();
  return 'off';
}

authService.onLogout(async () => {
  try {
    const contacts = await notificationApi.getContacts();
    await disablePushSubscription(contacts);
  } catch {
    // best-effort — logout shouldn't block on this
  }
});
